import React, { useState } from 'react';
import {
  Airplane,
  SquaresFour,
  Buildings,
  CaretDown,
  CaretUp,
  WarningCircle,
  Info,
  Target,
  Equals,
} from '@phosphor-icons/react';

/**
 * TriNodeHero — the hero visualization of DACAMS: the tri-node capacity equilibrium.
 *
 * CC (controlling capacity) = min(effRRC, RAC, effPTBC), each computed by an independent
 * module (RunCap / DACAMS core / AirCap). This component never compares the three values
 * itself to decide the bottleneck — that comparison (with its float-equality tie handling)
 * already happened server-side. We only read `part3.limiting_nodes`.
 *
 * Golden rule enforced here: a node's glass box (its computed breakdown) is only rendered
 * when its `source` says the value was actually computed (`runcap` / `aircap`). When the
 * source is `scalar_input` or `default`, we say so honestly instead of fabricating a chain
 * of subsystems that was never run.
 */

// ──────────────────────────────────────────────────────────────────────────
// Props contract
// ──────────────────────────────────────────────────────────────────────────

export interface TriNodeHeroProps {
  block7: any; // effRRC + effPTBC + their sources/breakdowns
  block4: any; // RAC + its formation chain
  block2: any; // CF (complexity factor)
  block3: any; // EF (efficiency factor)
  part3: any; // controlling_capacity, limiting_nodes, bci, svs, mismatch
  isDark: boolean;
  language: 'es' | 'en';
}

type NodeKey = 'rrc' | 'rac' | 'ptbc';
type NodeSource = 'runcap' | 'aircap' | 'scalar_input' | 'default' | 'error' | undefined;

// ──────────────────────────────────────────────────────────────────────────
// i18n
// ──────────────────────────────────────────────────────────────────────────

interface Strings {
  title: string;
  subtitle: string;
  ccEyebrow: string;
  unit: string;
  ccFormula: string;
  bci: string;
  svs: string;
  emptyState: string;
  emptyStateDesc: string;
  nodeNames: Record<NodeKey, string>;
  nodeAcronyms: Record<NodeKey, string>;
  origin: Record<NodeKey, string>;
  limiting: string;
  unconfigured: string;
  unconfiguredDesc: string;
  degraded: string;
  degradedDesc: string;
  sourceLabel: Record<string, string>;
  scalarNote: string;
  defaultNote: string;
  showBreakdown: string;
  hideBreakdown: string;
  method: string;
  fleetMixTitle: string;
  fleetMixDefaultNote: string;
  fleetMixNone: string;
  unknownShare: string;
  classified: string;
  unknown: string;
  total: string;
  racChainTitle: string;
  cmpInfra: string;
  cfComplexity: string;
  efEfficiency: string;
  aciAdj: string;
  racUncapped: string;
  infraCapped: string;
  ptbcBreakdownTitle: string;
  bottleneckDep: string;
  bottleneckArr: string;
  opsFactors: string;
  paxPerMovement: string;
  noSubsystemDetail: string;
  mismatch: string;
}

const STR: Record<'es' | 'en', Strings> = {
  es: {
    title: 'Equilibrio Tri-Nodal',
    subtitle: 'La capacidad del aeropuerto es el mínimo de tres nodos independientes',
    ccEyebrow: 'CAPACIDAD CONTROLADORA',
    unit: 'mov/h',
    ccFormula: 'CC = mín',
    bci: 'BCI',
    svs: 'SVS',
    emptyState: 'Ningún nodo configurado todavía',
    emptyStateDesc: 'Configura al menos un nodo (pista, plataforma o terminal) para calcular la capacidad controladora.',
    nodeNames: { rrc: 'Pista', rac: 'Plataforma', ptbc: 'Terminal' } as Record<NodeKey, string>,
    nodeAcronyms: { rrc: 'effRRC', rac: 'RAC', ptbc: 'effPTBC' } as Record<NodeKey, string>,
    origin: { rrc: 'vía RunCap', rac: 'vía DACAMS', ptbc: 'vía AirCap' } as Record<NodeKey, string>,
    limiting: 'Nodo limitante',
    unconfigured: 'No configurado',
    unconfiguredDesc: 'Este nodo no tiene capacidad configurada (≤ 0) y queda excluido del cálculo del mínimo.',
    degraded: 'Degradado',
    degradedDesc: 'El módulo de cálculo devolvió un error. Este valor no es fiable.',
    sourceLabel: {
      runcap: 'Calculado — RunCap',
      aircap: 'Calculado — AirCap',
      scalar_input: 'Valor manual',
      default: 'Valor por defecto',
      error: 'Error de cálculo',
    } as Record<string, string>,
    scalarNote: 'Valor escalar introducido a mano. Sin desglose de origen — no se muestra una cadena de subsistemas porque no existe.',
    defaultNote: 'Valor por defecto del sistema. No se ejecutó el módulo de cálculo, así que no hay desglose que enseñar.',
    showBreakdown: 'Ver desglose',
    hideBreakdown: 'Ocultar desglose',
    method: 'Método',
    fleetMixTitle: 'Mix de flota (RECAT-EU — estela)',
    fleetMixDefaultNote: 'RunCap usó su mix de flota por defecto, no el mix real del proyecto.',
    fleetMixNone: 'RunCap no reportó el mix de flota usado.',
    unknownShare: 'de la flota sin clasificar en RECAT-EU',
    classified: 'clasificados',
    unknown: 'desconocidos',
    total: 'total',
    racChainTitle: 'Cadena de formación RAC',
    cmpInfra: 'CMP infra',
    cfComplexity: 'CF complejidad',
    efEfficiency: 'EF eficiencia',
    aciAdj: 'ACI ajustado',
    racUncapped: 'RAC sin capar',
    infraCapped: 'La infraestructura de plataforma limita el techo de RAC',
    ptbcBreakdownTitle: 'Subsistemas terminal (AirCap)',
    bottleneckDep: 'Cuello de botella (salidas)',
    bottleneckArr: 'Cuello de botella (llegadas)',
    opsFactors: 'Factores operacionales',
    paxPerMovement: 'Pax / movimiento',
    noSubsystemDetail: 'AirCap no reportó subsistemas detallados para este cálculo.',
    mismatch: 'Holgura vs. mín',
  },
  en: {
    title: 'Tri-Node Equilibrium',
    subtitle: 'Airport capacity is the minimum of three independent nodes',
    ccEyebrow: 'CONTROLLING CAPACITY',
    unit: 'mov/h',
    ccFormula: 'CC = min',
    bci: 'BCI',
    svs: 'SVS',
    emptyState: 'No node configured yet',
    emptyStateDesc: 'Configure at least one node (runway, apron or terminal) to compute the controlling capacity.',
    nodeNames: { rrc: 'Runway', rac: 'Apron', ptbc: 'Terminal' } as Record<NodeKey, string>,
    nodeAcronyms: { rrc: 'effRRC', rac: 'RAC', ptbc: 'effPTBC' } as Record<NodeKey, string>,
    origin: { rrc: 'via RunCap', rac: 'via DACAMS', ptbc: 'via AirCap' } as Record<NodeKey, string>,
    limiting: 'Limiting node',
    unconfigured: 'Not configured',
    unconfiguredDesc: 'This node has no configured capacity (≤ 0) and is excluded from the minimum.',
    degraded: 'Degraded',
    degradedDesc: 'The calculation module returned an error. This value is not reliable.',
    sourceLabel: {
      runcap: 'Calculated — RunCap',
      aircap: 'Calculated — AirCap',
      scalar_input: 'Manual value',
      default: 'Default value',
      error: 'Calculation error',
    } as Record<string, string>,
    scalarNote: 'Scalar value entered by hand. No source breakdown — no subsystem chain is shown because none exists.',
    defaultNote: 'System default value. The calculation module never ran, so there is no breakdown to show.',
    showBreakdown: 'Show breakdown',
    hideBreakdown: 'Hide breakdown',
    method: 'Method',
    fleetMixTitle: 'Aircraft mix (RECAT-EU — wake)',
    fleetMixDefaultNote: "RunCap used its default fleet mix, not the project's real mix.",
    fleetMixNone: 'RunCap did not report the fleet mix used.',
    unknownShare: 'of the fleet could not be classified under RECAT-EU',
    classified: 'classified',
    unknown: 'unknown',
    total: 'total',
    racChainTitle: 'RAC formation chain',
    cmpInfra: 'CMP infra',
    cfComplexity: 'CF complexity',
    efEfficiency: 'EF efficiency',
    aciAdj: 'ACI adjusted',
    racUncapped: 'RAC uncapped',
    infraCapped: 'Apron infrastructure caps the RAC ceiling',
    ptbcBreakdownTitle: 'Terminal subsystems (AirCap)',
    bottleneckDep: 'Bottleneck (departures)',
    bottleneckArr: 'Bottleneck (arrivals)',
    opsFactors: 'Operational factors',
    paxPerMovement: 'Pax / movement',
    noSubsystemDetail: 'AirCap did not report detailed subsystems for this run.',
    mismatch: 'Slack vs. min',
  },
};

const NODE_TOKEN: Record<NodeKey, string> = {
  rrc: 'var(--node-rrc)',
  rac: 'var(--node-rac)',
  ptbc: 'var(--node-ptbc)',
};

const NODE_ICON: Record<NodeKey, React.ElementType> = {
  rrc: Airplane,
  rac: SquaresFour,
  ptbc: Buildings,
};

// ──────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────

function fmt(v: unknown, decimals = 1): string {
  if (v === null || v === undefined) return '—';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function fmtPct(v: unknown, decimals = 1): string {
  if (v === null || v === undefined) return '—';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return '—';
  // Accept either a 0-1 fraction or an already-scaled percentage.
  const pct = Math.abs(n) <= 1 ? n * 100 : n;
  return `${pct.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}%`;
}

const RECAT_ORDER = ['A', 'B', 'C', 'D', 'E', 'F'] as const;

// ──────────────────────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────────────────────

const TriNodeHero: React.FC<TriNodeHeroProps> = ({ block7, block4, block2, block3, part3, isDark, language }) => {
  const t = STR[language] ?? STR.es;
  const [expanded, setExpanded] = useState<Record<NodeKey, boolean>>({ rrc: false, rac: false, ptbc: false });

  const limitingNodes: NodeKey[] = Array.isArray(part3?.limiting_nodes) ? part3.limiting_nodes : [];
  const unconfiguredNodes: NodeKey[] = Array.isArray(part3?.unconfigured_nodes) ? part3.unconfigured_nodes : [];
  const cc = part3?.controlling_capacity;
  const hasAnyNode = limitingNodes.length > 0 || unconfiguredNodes.length < 3;

  const nodeValue: Record<NodeKey, number | undefined> = {
    rrc: block7?.rrc_input,
    rac: block4?.rac_capacity,
    ptbc: block7?.ptbc_input,
  };

  const nodeSource: Record<NodeKey, NodeSource> = {
    rrc: block7?.rrc_source,
    rac: undefined, // RAC has no "source" — DACAMS always computes it
    ptbc: block7?.ptbc_source,
  };

  const toggle = (k: NodeKey) => setExpanded((prev) => ({ ...prev, [k]: !prev[k] }));

  return (
    <div className="space-y-5">
      {/* ── Header ── */}
      <div>
        <div className="eyebrow flex items-center gap-1.5">
          <Target size={12} weight="bold" />
          {t.ccEyebrow}
        </div>
        <h3 className="text-lg font-bold" style={{ fontFamily: 'var(--font-display)', color: 'var(--text-primary)' }}>
          {t.title}
        </h3>
        <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
          {t.subtitle}
        </p>
      </div>

      {/* ── CC Hero strip ── */}
      <div
        className="pro-card p-5"
        style={{
          background: isDark
            ? 'linear-gradient(135deg, var(--surface-raised) 0%, var(--surface) 100%)'
            : 'linear-gradient(135deg, var(--surface-raised) 0%, var(--surface) 100%)',
        }}
      >
        {!hasAnyNode ? (
          <div className="flex items-center gap-3 py-2">
            <WarningCircle size={24} className="shrink-0" style={{ color: 'var(--text-tertiary)' }} />
            <div>
              <div className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>
                {t.emptyState}
              </div>
              <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                {t.emptyStateDesc}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row lg:items-center gap-6">
            {/* CC value */}
            <div className="flex items-baseline gap-3 shrink-0">
              <span
                className="font-mono tabular-nums font-bold leading-none"
                style={{ fontSize: '2.75rem', color: 'var(--text-primary)' }}
              >
                {fmt(cc, 1)}
              </span>
              <span className="text-sm font-medium" style={{ color: 'var(--text-tertiary)' }}>
                {t.unit}
              </span>
            </div>

            {/* Formula: CC = min(rrc, rac, ptbc) */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                {t.ccFormula}
              </span>
              <Equals size={12} weight="bold" style={{ color: 'var(--text-tertiary)' }} />
              {(['rrc', 'rac', 'ptbc'] as NodeKey[]).map((k, i) => {
                const isLimiting = limitingNodes.includes(k);
                const isUnconf = unconfiguredNodes.includes(k);
                return (
                  <React.Fragment key={k}>
                    {i > 0 && (
                      <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                        ,
                      </span>
                    )}
                    <span
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-mono tabular-nums font-semibold"
                      style={{
                        color: isUnconf ? 'var(--text-tertiary)' : NODE_TOKEN[k],
                        background: isLimiting ? 'var(--aipron-accent-muted)' : 'var(--surface-inset)',
                        border: isLimiting ? '1px solid var(--aipron-accent)' : '1px solid transparent',
                        opacity: isUnconf ? 0.5 : 1,
                        textDecoration: isUnconf ? 'line-through' : 'none',
                      }}
                      title={t.nodeNames[k]}
                    >
                      {t.nodeAcronyms[k]}={isUnconf ? '—' : fmt(nodeValue[k], 1)}
                    </span>
                  </React.Fragment>
                );
              })}
            </div>

            {/* BCI / SVS */}
            <div className="flex items-center gap-3 lg:ml-auto">
              <div className="text-right">
                <div className="kpi-label">{t.bci}</div>
                <div className="font-mono tabular-nums font-bold text-lg" style={{ color: 'var(--text-primary)' }}>
                  {fmt(part3?.bci, 2)}
                </div>
              </div>
              <div className="w-px h-8" style={{ background: 'var(--border)' }} />
              <div className="text-right">
                <div className="kpi-label">{t.svs}</div>
                <div className="font-mono tabular-nums font-bold text-lg" style={{ color: 'var(--text-primary)' }}>
                  {part3?.svs_pct !== undefined && part3?.svs_pct !== null ? fmtPct(part3.svs_pct, 1) : fmt(part3?.svs, 2)}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Node cards ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {(['rrc', 'rac', 'ptbc'] as NodeKey[]).map((k) => (
          <NodeCard
            key={k}
            nodeKey={k}
            value={nodeValue[k]}
            source={nodeSource[k]}
            isLimiting={limitingNodes.includes(k)}
            isUnconfigured={unconfiguredNodes.includes(k)}
            isExpanded={expanded[k]}
            onToggle={() => toggle(k)}
            t={t}
            block7={block7}
            block4={block4}
            block2={block2}
            block3={block3}
            mismatch={k === 'rrc' ? part3?.mismatch_rwy : k === 'rac' ? part3?.mismatch_apron : part3?.mismatch_terminal}
          />
        ))}
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// NodeCard
// ──────────────────────────────────────────────────────────────────────────

interface NodeCardProps {
  nodeKey: NodeKey;
  value: number | undefined;
  source: NodeSource;
  isLimiting: boolean;
  isUnconfigured: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  t: Strings;
  block7: any;
  block4: any;
  block2: any;
  block3: any;
  mismatch: number | undefined;
}

const NodeCard: React.FC<NodeCardProps> = ({
  nodeKey,
  value,
  source,
  isLimiting,
  isUnconfigured,
  isExpanded,
  onToggle,
  t,
  block7,
  block4,
  block2,
  block3,
  mismatch,
}) => {
  const token = NODE_TOKEN[nodeKey];
  const Icon = NODE_ICON[nodeKey];
  const isDegraded = source === 'error';
  const hasGlassBox = nodeKey === 'rac' || source === 'runcap' || source === 'aircap';
  const detail: string | undefined = nodeKey === 'rrc' ? block7?.rrc_detail : nodeKey === 'ptbc' ? block7?.ptbc_detail : undefined;

  return (
    <div
      className="pro-card p-4 flex flex-col"
      style={{
        borderColor: isLimiting ? 'var(--aipron-accent)' : isUnconfigured ? 'var(--border-subtle)' : 'var(--card-border)',
        borderWidth: isLimiting ? '1.5px' : '1px',
        borderStyle: isUnconfigured ? 'dashed' : 'solid',
        opacity: isUnconfigured ? 0.65 : 1,
        boxShadow: isLimiting ? '0 0 0 3px var(--aipron-accent-muted)' : undefined,
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="inline-flex items-center justify-center w-8 h-8 rounded-lg shrink-0"
            style={{ background: `color-mix(in srgb, ${token} 16%, transparent)`, color: token }}
          >
            <Icon size={17} weight="bold" />
          </span>
          <div className="min-w-0">
            <div className="text-sm font-bold truncate" style={{ fontFamily: 'var(--font-display)', color: 'var(--text-primary)' }}>
              {t.nodeNames[nodeKey]}
            </div>
            <div className="text-2xs font-mono uppercase tracking-wide" style={{ color: token }}>
              {t.nodeAcronyms[nodeKey]}
            </div>
          </div>
        </div>
        {isLimiting && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-2xs font-bold uppercase tracking-wider shrink-0"
            style={{ background: 'var(--aipron-accent-muted)', color: 'var(--aipron-accent)' }}
          >
            {t.limiting}
          </span>
        )}
      </div>

      {/* Value */}
      <div className="mt-3 flex items-baseline gap-2">
        <span className="font-mono tabular-nums font-bold text-2xl" style={{ color: isUnconfigured ? 'var(--text-tertiary)' : 'var(--text-primary)' }}>
          {isUnconfigured ? '—' : fmt(value, 1)}
        </span>
        <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
          {t.unit}
        </span>
      </div>

      {/* Origin + source badge */}
      <div className="mt-1.5 flex items-center gap-2 flex-wrap">
        <span className="text-2xs font-semibold" style={{ color: 'var(--text-tertiary)' }}>
          {t.origin[nodeKey]}
        </span>
        {source && (
          <span
            className={isDegraded ? 'badge-danger' : source === 'runcap' || source === 'aircap' ? 'badge-info' : 'badge-neutral'}
          >
            {t.sourceLabel[source] ?? source}
          </span>
        )}
      </div>

      {/* Unconfigured / degraded honest states */}
      {isUnconfigured && (
        <div className="mt-2 text-2xs leading-snug" style={{ color: 'var(--text-tertiary)' }}>
          {t.unconfiguredDesc}
        </div>
      )}
      {isDegraded && !isUnconfigured && (
        <div className="mt-2 flex items-start gap-1.5 text-2xs leading-snug" style={{ color: 'var(--aipron-danger)' }}>
          <WarningCircle size={13} className="shrink-0 mt-0.5" />
          {t.degradedDesc}
        </div>
      )}

      {/* Detail caption */}
      {detail && !isUnconfigured && (
        <div className="mt-2 flex items-start gap-1.5 text-2xs leading-snug" style={{ color: 'var(--text-secondary)' }}>
          <Info size={13} className="shrink-0 mt-0.5" style={{ color: 'var(--text-tertiary)' }} />
          <span>{detail}</span>
        </div>
      )}

      {/* Mismatch (slack vs min) */}
      {!isUnconfigured && mismatch !== undefined && mismatch !== null && (
        <div className="mt-2 text-2xs font-mono tabular-nums" style={{ color: 'var(--text-tertiary)' }}>
          {t.mismatch}: <span style={{ color: 'var(--text-secondary)' }}>{fmt(mismatch, 1)}</span>
        </div>
      )}

      {/* Glass box toggle */}
      {!isUnconfigured && (
        <button
          type="button"
          onClick={onToggle}
          className="mt-3 flex items-center gap-1 text-2xs font-semibold self-start hover:underline"
          style={{ color: token }}
        >
          {isExpanded ? t.hideBreakdown : t.showBreakdown}
          {isExpanded ? <CaretUp size={12} /> : <CaretDown size={12} />}
        </button>
      )}

      {/* Glass box content */}
      {isExpanded && !isUnconfigured && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--border-subtle)' }}>
          {nodeKey === 'rrc' && <RrcGlassBox block7={block7} source={source} t={t} token={token} />}
          {nodeKey === 'rac' && <RacGlassBox block4={block4} block2={block2} block3={block3} t={t} token={token} />}
          {nodeKey === 'ptbc' && <PtbcGlassBox block7={block7} source={source} t={t} token={token} />}
        </div>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// RRC glass box — RunCap method + RECAT-EU fleet mix
// ──────────────────────────────────────────────────────────────────────────

const RrcGlassBox: React.FC<{ block7: any; source: NodeSource; t: Strings; token: string }> = ({
  block7,
  source,
  t,
  token,
}) => {
  if (source !== 'runcap') {
    return (
      <div className="text-2xs leading-snug" style={{ color: 'var(--text-tertiary)' }}>
        {source === 'default' ? t.defaultNote : t.scalarNote}
      </div>
    );
  }

  const method = block7?.runcap_method;
  const mix = block7?.rrc_fleet_mix as Record<string, number> | null | undefined;
  const stats = block7?.rrc_fleet_mix_stats as
    | { total?: number; classified?: number; unknown?: number; unknown_share?: number; unknown_types?: string[] }
    | null
    | undefined;
  const unknownShare = stats?.unknown_share;
  const showUnknownWarning = typeof unknownShare === 'number' && unknownShare > 0.1;

  return (
    <div className="space-y-3">
      {method && (
        <div className="flex items-center justify-between text-2xs">
          <span style={{ color: 'var(--text-tertiary)' }}>{t.method}</span>
          <span className="font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>
            {method}
          </span>
        </div>
      )}

      <div>
        <div className="text-2xs font-bold uppercase tracking-wider mb-1.5" style={{ color: token }}>
          {t.fleetMixTitle}
        </div>

        {!mix ? (
          <div className="text-2xs leading-snug" style={{ color: 'var(--text-tertiary)' }}>
            {t.fleetMixDefaultNote}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-6 gap-1">
              {RECAT_ORDER.map((cat) => {
                const key = Object.keys(mix).find((mk) => mk.toUpperCase() === cat || mk.toUpperCase() === `CAT_${cat}`);
                const v = key ? mix[key] : mix[cat];
                return (
                  <div key={cat} className="text-center rounded p-1.5" style={{ background: 'var(--surface-inset)' }}>
                    <div className="text-2xs font-bold" style={{ color: 'var(--text-tertiary)' }}>
                      {cat}
                    </div>
                    <div className="font-mono tabular-nums text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                      {v !== undefined ? fmtPct(v, 0) : '—'}
                    </div>
                  </div>
                );
              })}
            </div>

            {stats && (
              <div className="mt-2 text-2xs font-mono tabular-nums" style={{ color: 'var(--text-tertiary)' }}>
                {stats.classified ?? '—'}/{stats.total ?? '—'} {t.classified}
                {typeof stats.unknown === 'number' && stats.unknown > 0 && (
                  <>
                    {' · '}
                    {stats.unknown} {t.unknown}
                  </>
                )}
              </div>
            )}

            {showUnknownWarning && (
              <div className="mt-2 flex items-start gap-1.5 text-2xs leading-snug" style={{ color: 'var(--aipron-warning)' }}>
                <WarningCircle size={13} className="shrink-0 mt-0.5" />
                <span>
                  {fmtPct(unknownShare, 1)} {t.unknownShare}
                  {stats?.unknown_types && stats.unknown_types.length > 0 ? `: ${stats.unknown_types.join(', ')}` : ''}
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// RAC glass box — CMP infra × CF × EF → ACI adj → RAC
// ──────────────────────────────────────────────────────────────────────────

const RacGlassBox: React.FC<{ block4: any; block2: any; block3: any; t: Strings; token: string }> = ({
  block4,
  block2,
  block3,
  t,
  token,
}) => {
  const cmpInfra = block4?.cmp_infra;
  const cf = block2?.cf_total;
  const ef = block3?.ef_factor;
  const aciAdj = block4?.aci_adj;
  const racUncapped = block4?.rac_uncapped;
  const racCapped = block4?.rac_capped_by_infra;
  const racCapacity = block4?.rac_capacity;

  const steps: { label: string; value: unknown; decimals?: number }[] = [
    { label: t.cmpInfra, value: cmpInfra },
    { label: t.cfComplexity, value: cf, decimals: 2 },
    { label: t.efEfficiency, value: ef, decimals: 2 },
    { label: t.aciAdj, value: aciAdj },
  ];

  return (
    <div className="space-y-2">
      <div className="text-2xs font-bold uppercase tracking-wider" style={{ color: token }}>
        {t.racChainTitle}
      </div>
      <div className="flex items-center flex-wrap gap-1.5">
        {steps.map((s, i) => (
          <React.Fragment key={s.label}>
            {i > 0 && (
              <span className="text-2xs" style={{ color: 'var(--text-tertiary)' }}>
                →
              </span>
            )}
            <div className="text-center rounded px-2 py-1" style={{ background: 'var(--surface-inset)' }}>
              <div className="text-2xs" style={{ color: 'var(--text-tertiary)' }}>
                {s.label}
              </div>
              <div className="font-mono tabular-nums text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                {fmt(s.value, s.decimals ?? 1)}
              </div>
            </div>
          </React.Fragment>
        ))}
        <span className="text-2xs" style={{ color: 'var(--text-tertiary)' }}>
          →
        </span>
        <div className="text-center rounded px-2 py-1" style={{ background: 'var(--aipron-accent-muted)' }}>
          <div className="text-2xs" style={{ color: 'var(--text-tertiary)' }}>
            RAC
          </div>
          <div className="font-mono tabular-nums text-xs font-bold" style={{ color: token }}>
            {fmt(racCapacity, 1)}
          </div>
        </div>
      </div>

      {racCapped && (
        <div className="flex items-start gap-1.5 text-2xs leading-snug" style={{ color: 'var(--aipron-warning)' }}>
          <WarningCircle size={13} className="shrink-0 mt-0.5" />
          <span>
            {t.infraCapped}
            {racUncapped !== undefined && racUncapped !== null ? ` (${t.racUncapped}: ${fmt(racUncapped, 1)})` : ''}
          </span>
        </div>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// PTBC glass box — AirCap subsystem breakdown
// ──────────────────────────────────────────────────────────────────────────

const PtbcGlassBox: React.FC<{ block7: any; source: NodeSource; t: Strings; token: string }> = ({
  block7,
  source,
  t,
  token,
}) => {
  if (source !== 'aircap') {
    return (
      <div className="text-2xs leading-snug" style={{ color: 'var(--text-tertiary)' }}>
        {source === 'default' ? t.defaultNote : t.scalarNote}
      </div>
    );
  }

  const bd = block7?.ptbc_breakdown as
    | {
        subsystems?: any[];
        bottleneck_dep?: string;
        bottleneck_arr?: string;
        ops_factors?: Record<string, unknown>;
        pax_per_movement?: number;
      }
    | null
    | undefined;

  if (!bd) {
    return (
      <div className="text-2xs leading-snug" style={{ color: 'var(--text-tertiary)' }}>
        {t.noSubsystemDetail}
      </div>
    );
  }

  const subsystems = Array.isArray(bd.subsystems) ? bd.subsystems : [];

  return (
    <div className="space-y-2">
      <div className="text-2xs font-bold uppercase tracking-wider" style={{ color: token }}>
        {t.ptbcBreakdownTitle}
      </div>

      {subsystems.length === 0 ? (
        <div className="text-2xs leading-snug" style={{ color: 'var(--text-tertiary)' }}>
          {t.noSubsystemDetail}
        </div>
      ) : (
        <div className="space-y-1">
          {subsystems.map((s: any, i: number) => {
            const name = s?.name ?? s?.label ?? s?.id ?? `#${i + 1}`;
            const cap = s?.capacity ?? s?.value ?? s?.cap;
            const isBottleneckDep = bd.bottleneck_dep && String(bd.bottleneck_dep) === String(s?.id ?? s?.name);
            const isBottleneckArr = bd.bottleneck_arr && String(bd.bottleneck_arr) === String(s?.id ?? s?.name);
            return (
              <div
                key={i}
                className="flex items-center justify-between text-2xs rounded px-2 py-1"
                style={{
                  background: isBottleneckDep || isBottleneckArr ? 'var(--aipron-accent-muted)' : 'var(--surface-inset)',
                }}
              >
                <span className="truncate" style={{ color: 'var(--text-secondary)' }}>
                  {String(name)}
                  {isBottleneckDep && <span style={{ color: 'var(--aipron-accent)' }}> · {t.bottleneckDep}</span>}
                  {isBottleneckArr && <span style={{ color: 'var(--aipron-accent)' }}> · {t.bottleneckArr}</span>}
                </span>
                <span className="font-mono tabular-nums font-semibold shrink-0 ml-2" style={{ color: 'var(--text-primary)' }}>
                  {cap !== undefined ? fmt(cap, 1) : '—'}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {bd.pax_per_movement !== undefined && bd.pax_per_movement !== null && (
        <div className="flex items-center justify-between text-2xs">
          <span style={{ color: 'var(--text-tertiary)' }}>{t.paxPerMovement}</span>
          <span className="font-mono tabular-nums font-semibold" style={{ color: 'var(--text-primary)' }}>
            {fmt(bd.pax_per_movement, 1)}
          </span>
        </div>
      )}

      {bd.ops_factors && Object.keys(bd.ops_factors).length > 0 && (
        <div>
          <div className="text-2xs mb-1" style={{ color: 'var(--text-tertiary)' }}>
            {t.opsFactors}
          </div>
          <div className="flex flex-wrap gap-1">
            {Object.entries(bd.ops_factors).map(([k, v]) => (
              <span
                key={k}
                className="text-2xs font-mono tabular-nums px-1.5 py-0.5 rounded"
                style={{ background: 'var(--surface-inset)', color: 'var(--text-secondary)' }}
              >
                {k}={typeof v === 'number' ? fmt(v, 2) : String(v)}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TriNodeHero;
